import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SidebarFilter from 'components/artectum/sidebar-filters/sidebar-filters';
import Slider from 'react-slick';
import { categoriesSliderSettings } from 'core/config/slides';
import { useProjects } from 'core/hooks/useProjects';
import useFetch from 'core/hooks/api/useFetch';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import PlaceholderCategoryItem from '../cards/PlaceholderCategoryItem';
import Pagination from './Pagination';
import ProjectGrid from './ProjectGrid';
import PlaceholderCard from '../cards/PlaceholderCard';
import CategoryItem from '../cards/category-card';
import { useFilters } from 'core/hooks/useFilters';
import { useFiltersCards } from 'core/context/filtersCardsContext';
import { getData } from 'core/services/api';
import { Helmet } from 'react-helmet';

const formatCategoryValue = (value) => {
    return value.replace(/[-_]/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const GridPortfolio = () => {
    const location = useLocation();
    const [selectedFilters, setSelectedFilters] = useState({});
    const [projectsData, setProjectsData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState({});
    const { data: projectFilter, loading: loadingProjectFilters } = useFetch(API_ENDPOINTS.PROJECT_FILTERS);
    const { data: filter, loading: loadingFilters } = useFetch(API_ENDPOINTS.FILTERS);
    const { getProjectCategories, registerProjectCategories } = useFiltersCards();
    const projectCategories = getProjectCategories();
    const { data: fetchedProjectCategories, error: projectCategoriesError } = useFetch(
        projectCategories ? null : API_ENDPOINTS.FILTER + "/search/project_filters"
    );

    useEffect(() => {
        if (!projectCategories && fetchedProjectCategories) {
            registerProjectCategories(fetchedProjectCategories);
        }
    }, [projectCategories, fetchedProjectCategories, registerProjectCategories]);

    const { currentProjects, currentPage, pageCount, handlePageChange, setCurrentPage } = useProjects(projectsData, setSelectedFilters);

    const validFilters = useMemo(() =>
        filter
        ?.filter(f => projectFilter?.some(pf => pf.filter_id === f.id))
        ?.filter(f => f.name !== 'project_filters' && f.name !== 'product_filters'&& f.name !== 'tendencias'),
        [filter, projectFilter]
    );

    const { filters, selectedCategory, setSelectedCategory, handleFilterChange, setFilters, resetFilters } = useFilters(validFilters);

    const searchStats = {
        resultCount: projectsData ? projectsData.total : 0,
        searchTime: 0.62
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const initialFilters = {};

        params.forEach((value, key) => {
            if (!key.toLowerCase().includes('utm')) {
                initialFilters[`filters[${key}]`] = value;
            }
        });

        setSelectedFilters(initialFilters);

        const updatedFilters = validFilters?.map(f => {
            const filterKey = `filters[${f.name.toLowerCase()}]`;
            if (initialFilters[filterKey]) {
                const values = initialFilters[filterKey].split(',');
                return {
                    ...f,
                    options: f.options?.map(option => ({
                        ...option,
                        checked: values.includes(option.value)
                    })),
                    defaultOpen: true // Expand sections with active filters
                };
            }
            return f;
        }) || [];

        setFilters(updatedFilters);

        fetchProjects(initialFilters);
    }, [location.search, validFilters]);

    const handleFilterChangeWithRefetch = (sectionTitle, value, checked, type) => {
        handleFilterChange(sectionTitle, value, checked);
        setSelectedFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            const filterKey = `filters[${sectionTitle.toLowerCase()}]`;
            const currentValues = newFilters[filterKey] ? newFilters[filterKey].split(',') : [];

            if (type === 'single') {
                if (checked) {
                    newFilters[filterKey] = value;
                } else {
                    delete newFilters[filterKey];
                }
            } else {
                if (checked) {
                    currentValues.push(value);
                } else {
                    const index = currentValues.indexOf(value);
                    if (index > -1) {
                        currentValues.splice(index, 1);
                    }
                }

                if (currentValues.length > 0) {
                    newFilters[filterKey] = currentValues.join(',');
                } else {
                    delete newFilters[filterKey];
                }
            }

            // Reset pagination to page 1
            newFilters.page = 1;
            setCurrentPage(1);

            fetchProjects(newFilters);
            return newFilters;
        });
    };

    const handleClearFilters = () => {
        setSelectedFilters({});
        resetFilters();
        setSelectedCategory('');
    };

    const handleCategorySelect = (categoryValue) => {
        if (categoryValue === 'Todos los proyectos') {
            setSelectedCategory('');
            const newFilters = {};
            setSelectedFilters(newFilters);
            resetFilters();
            setCurrentPage(1); // Reset pagination to page 1
            fetchProjects(newFilters);
        } else {
            const formattedCategoryValue = categoryValue.toLowerCase().replace(/\s+/g, '_');
            setSelectedCategory(formattedCategoryValue);
            setSelectedFilters(prevFilters => {
                const newFilters = {
                    ...prevFilters,
                    'filters[project_filters]': formattedCategoryValue,
                    page: 1 // Reset pagination to page 1
                };
                setCurrentPage(1); // Reset pagination to page 1
                fetchProjects(newFilters);
                return newFilters;
            });
        }
    };

    const fetchProjects = async (filters) => {
        setIsLoading(true);
        try {
            const endpoint = `${API_ENDPOINTS.PROJECT_PAGINATED}`;
            const response = Object.keys(filters).length > 0
                ? await getData(endpoint, filters)
                : await getData(endpoint);
            setProjectsData(response.data);
        } catch (error) {
            console.error('Error fetching projects:', error);
            setError(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="ltn__product-area ltn__product-gutter">
            <Helmet>
                <title>Proyectos con Baldosas y Mosaicos | Artectum</title>
                <meta name="description" content="Conoce nuestros proyectos en construcción y diseño con baldosas, terrazos y celosías de concreto. Inspírate y cotiza el tuyo." />
            </Helmet>
            <div className="container">
                <div className="row mt-5" style={{ position: 'relative' }}>
                    <div className="col-lg-9 order-lg-2 mb-100" style={{ minHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}>
                        <div className="ltn__shop-options mb-0 bg-white p-0">
                            <div className="col-12 mb-2 p-0">
                                <h6 className="fw-bold mb-3 pt-3">¿Qué estas buscando?</h6>
                                <Slider {...categoriesSliderSettings} className="slick-arrow-5 hidden-mob">
                                    <CategoryItem
                                        key="all-projects"
                                        category={{ value: 'Todos los proyectos', icon: 'fas fa-th-large' }}
                                        isSelected={selectedCategory === ''}
                                        onSelect={handleCategorySelect}
                                    />
                                    {projectCategories ? (
                                        projectCategories[0]?.options?.map((category) => (
                                            <CategoryItem
                                                key={category.id}
                                                category={{ ...category, value: formatCategoryValue(category.value) }}
                                                isSelected={selectedCategory === category.value}
                                                onSelect={handleCategorySelect}
                                            />
                                        ))
                                    ) : (
                                        [...Array(7)].map((_, index) => (
                                            <div className="col-md-3 col-sm-6 col-6" key={index}>
                                                <PlaceholderCategoryItem />
                                            </div>
                                        ))
                                    )}
                                </Slider>
                            </div>
                        </div>

                        <div className="tab-content">
                            <div className="tab-pane fade active show" id="liton_product_grid">
                                <div className="ltn__product-tab-content-inner ltn__product-grid-view">
                                    {isLoading ? (
                                        <div className="row d-flex justify-content-center">
                                            {[...Array(4)].map((_, index) => (
                                                <div className="col-md-3 col-sm-6 col-6" key={index}>
                                                    <PlaceholderCard />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <ProjectGrid projects={currentProjects} />
                                    )}
                                </div>
                            </div>
                        </div>

                        <Pagination
                            currentPage={currentPage}
                            pageCount={pageCount}
                            onPageChange={(page) => {
                                handlePageChange(page); // Update page state
                                fetchProjects({ ...selectedFilters, page }); // Trigger fetch with updated page
                            }}
                        />
                    </div>
                    <SidebarFilter
                        filters={filters}
                        searchStats={searchStats}
                        loading={loadingProjectFilters || loadingFilters}
                        onFilterChange={(sectionTitle, value, checked) => handleFilterChangeWithRefetch(sectionTitle, value, checked, filters.find(f => f.name === sectionTitle).type)}
                        title="Filtros de Búsqueda"
                        columnSize="col-lg-3"
                        onClearFilters={handleClearFilters}
                    />
                </div>
            </div>
        </div>
    );
};

export default GridPortfolio;